import React, { useState } from 'react';
import {
    Stack,
    Row,
    FieldGroup,
    PhoneInput,
    TextInput,
    PasswordInput,
    PillButton,
    ErrorText,
    TextButton,
    Caption,
    Text,
} from 'library/ui';
import {
    sendPasswordResetCode,
    checkPasswordResetCode,
    recruitChangePassword,
} from 'recruit/api/candidate_auth';
import { setRecruitToken, useRecruitCandidateUser } from 'recruit/context/RecruitCandidateUserContext';

type Step = 'phone' | 'code' | 'password';

interface CandidateForgotPasswordFormProps {
    onSuccess: () => void;
    onBackToLogin: () => void;
}

function toPhoneDigits(phone: string) {
    const digits = phone.replace(/\D/g, '');
    return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
}

function maskPhone(phone: string) {
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 4) return phone;
    return '(***) ***-' + digits.slice(-4);
}

export function CandidateForgotPasswordForm({ onSuccess, onBackToLogin }: CandidateForgotPasswordFormProps) {
    const [step, setStep] = useState<Step>('phone');
    const [phone, setPhone] = useState('');
    const [code, setCode] = useState('');
    const [resetToken, setResetToken] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [resending, setResending] = useState(false);
    const [resent, setResent] = useState(false);
    const { refreshCandidateUser } = useRecruitCandidateUser();

    async function handleSendCode() {
        setError('');
        const digits = toPhoneDigits(phone);
        if (digits.length !== 10) {
            setError('Please enter a valid phone number.');
            return;
        }
        setLoading(true);
        try {
            await sendPasswordResetCode(digits);
            setStep('code');
        } catch {
            setError('Failed to send code. Please try again.');
        } finally {
            setLoading(false);
        }
    }

    async function handleVerifyCode() {
        setError('');
        if (!code) {
            setError('Please enter the verification code.');
            return;
        }
        setLoading(true);
        try {
            const res = await checkPasswordResetCode(toPhoneDigits(phone), code);
            if (res.status === 'approved' && res.token) {
                setResetToken(res.token);
                setStep('password');
            } else {
                setError('Invalid verification code. Please try again.');
            }
        } catch {
            setError('Invalid verification code. Please try again.');
        } finally {
            setLoading(false);
        }
    }

    async function handleResend() {
        setResending(true);
        setResent(false);
        try {
            await sendPasswordResetCode(toPhoneDigits(phone));
            setResent(true);
        } catch {
            setError('Failed to resend code. Please try again.');
        } finally {
            setResending(false);
        }
    }

    async function handleResetPassword() {
        setError('');
        if (password.length < 8) {
            setError('Password must be at least 8 characters.');
            return;
        }
        if (password !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }
        setLoading(true);
        try {
            await recruitChangePassword(password, resetToken);
            setRecruitToken(resetToken);
            await refreshCandidateUser();
            onSuccess();
        } catch {
            setError('Failed to reset your password. Please try again.');
            setLoading(false);
        }
    }

    if (step === 'phone') {
        return (
            <>
                <Text component="p" lineHeight="relaxed">
                    Enter the phone number on your account and we&apos;ll text you a one-time code to reset your
                    password.
                </Text>
                <Stack gap="md">
                    <FieldGroup label="Phone Number">
                        <PhoneInput value={phone} onChange={(e164) => setPhone(e164)} fullWidth />
                    </FieldGroup>
                    {error && <ErrorText>{error}</ErrorText>}
                    <PillButton onClick={handleSendCode} disabled={loading}>
                        {loading ? 'Sending...' : 'Send Code'}
                    </PillButton>
                </Stack>
                <Row gap="sm" justify="center">
                    <TextButton onClick={onBackToLogin}>Back to log in</TextButton>
                </Row>
            </>
        );
    }

    if (step === 'code') {
        return (
            <>
                <Text component="p" lineHeight="relaxed">
                    If an account exists for <strong>{maskPhone(phone)}</strong>, we sent it a verification code.
                </Text>
                <Stack gap="md">
                    <FieldGroup label="Verification Code">
                        <TextInput value={code} onChange={setCode} placeholder="Enter your code" fullWidth />
                    </FieldGroup>
                    {error && <ErrorText>{error}</ErrorText>}
                    <PillButton onClick={handleVerifyCode} disabled={loading}>
                        {loading ? 'Verifying...' : 'Verify'}
                    </PillButton>
                </Stack>
                <Row gap="sm" justify="center">
                    <TextButton onClick={handleResend} disabled={resending}>
                        {resending ? 'Resending...' : 'Resend code'}
                    </TextButton>
                    {resent && <Caption>Code resent!</Caption>}
                </Row>
            </>
        );
    }

    return (
        <>
            <Text component="p" lineHeight="relaxed">
                Enter a new password for your account.
            </Text>
            <Stack gap="md">
                <FieldGroup label="New Password">
                    <PasswordInput
                        value={password}
                        onChange={setPassword}
                        placeholder="Enter your new password"
                        fullWidth
                    />
                </FieldGroup>
                <FieldGroup label="Confirm Password">
                    <PasswordInput
                        value={confirmPassword}
                        onChange={setConfirmPassword}
                        placeholder="Confirm your new password"
                        fullWidth
                    />
                </FieldGroup>
                {error && <ErrorText>{error}</ErrorText>}
                <PillButton onClick={handleResetPassword} disabled={loading}>
                    {loading ? 'Resetting...' : 'Reset Password'}
                </PillButton>
            </Stack>
        </>
    );
}
